"use client"

import { useState, useEffect } from "react"
import { getAuth } from "firebase/auth"
import { getDatabase, ref as dbRef, push, onValue, runTransaction } from "firebase/database"
import "./RaiseIssuePage.css"
import { useLocation } from "react-router-dom"
import { FaInfoCircle, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa"
import { db, ref, get, set } from "../Firebase/firebase-config"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"

const MAX_ISSUES_PER_DAY = 10

const RaiseIssuePage = () => {
  const location = useLocation()
  const orderDetails = location.state || {}
  const { userId } = useGetCurrentUserId()

  const [issueType, setIssueType] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState("")
  const [attachment, setAttachment] = useState(null)
  const [contactInfo, setContactInfo] = useState({ name: "", email: "" })
  const [confirmation, setConfirmation] = useState(false)
  const [issuesHistory, setIssuesHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [companyName, setCompanyName] = useState("")
  const [siteAddress, setSiteAddress] = useState(orderDetails.siteAddress || "")
  const [brand, setBrand] = useState(orderDetails.brand || "")
  const [grade, setGrade] = useState(orderDetails.grade || "")
  const [dispatchDetails, setDispatchDetails] = useState(orderDetails.dispatchDetails || "")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [showIssues, setShowIssues] = useState(false)
  const [remainingIssues, setRemainingIssues] = useState(MAX_ISSUES_PER_DAY)

  const getTodayDateKey = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000
    const istDate = new Date(now.getTime() + istOffset)
    istDate.setHours(0, 0, 0, 0)
    return istDate.toISOString().split("T")[0]
  }

  // Daily limit listener
  useEffect(() => {
    if (!userId) return
    const today = getTodayDateKey()
    const countRef = dbRef(getDatabase(), `limit_counter/daily_issues_counts/${userId}/${today}`)

    const unsubscribe = onValue(countRef, (snapshot) => {
      const count = snapshot.val() || 0
      setRemainingIssues(Math.max(0, MAX_ISSUES_PER_DAY - count))
    })

    return () => unsubscribe()
  }, [userId])

  // Auth user info
  useEffect(() => {
    const auth = getAuth()
    const user = auth.currentUser
    if (user) {
      setContactInfo({
        name: user.username || "",
        email: user.email || "",
      })
    }
  }, [])

  // Fetch company name
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }
      try {
        const profileRef = ref(db, `profileDashboard/${userId}`)
        const snapshot = await get(profileRef)
        if (snapshot.exists()) {
          const profileData = snapshot.val()
          setCompanyName(profileData.company_name || "Unknown Company")
        }
      } catch (err) {
        console.error("Error fetching user data:", err)
        setError("Error fetching company information")
      } finally {
        setLoading(false)
      }
    }
    fetchUserData()
  }, [userId])

  // Sync order details
  useEffect(() => {
    if (orderDetails) {
      setBrand(orderDetails.brand || "")
      setGrade(orderDetails.grade || "")
      setSiteAddress(orderDetails.siteAddress || "")
      setDispatchDetails(orderDetails.dispatchDetails || "")
    }
  }, [orderDetails])

  // Fetch issue history
  useEffect(() => {
    if (!userId) return

    const database = getDatabase()
    const issuesRef = dbRef(database, `issues/${userId}`)

    const unsubscribe = onValue(issuesRef, (snapshot) => {
      const data = snapshot.val()
      let issues = data ? Object.values(data) : []

      issues.sort((a, b) => {
        if (a.status === "Pending" && b.status !== "Pending") return -1
        if (a.status !== "Pending" && b.status === "Pending") return 1
        return 0
      })

      setIssuesHistory(issues)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [userId])

  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const sendNotification = async (issuesId, companyName) => {
    if (!userId) return
    const notificationRef = ref(db, `supplier_notifications/Raised Issues/${userId}/${issuesId}`)
    const gifUrl = "Images/New notification.gif"

    const newNotification = {
      title: "Issues",
      brand,
      siteAddress,
      issueType,
      message: `A new issue has been sent by ${companyName}. Please review the issue.`,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (remainingIssues <= 0) {
      setError("You have reached the daily limit of 10 issues. Please try again tomorrow.")
      return
    }

    const cleaned = description.replace(/\s/g, "")
    if (cleaned.length < 10 || cleaned.length > 100) {
      setError("Description must be between 10 and 100 characters.")
      return
    }

    if (!userId) {
      setError("User not authenticated")
      return
    }

    if (!issueType || !description || !brand || !grade) return

    const database = getDatabase()
    const issuesRef = dbRef(database, `issues/${userId}`)
    const today = getTodayDateKey()
    const countRef = dbRef(database, `limit_counter/daily_issues_counts/${userId}/${today}`)

    try {
      await runTransaction(countRef, (current) => {
        const cnt = current || 0
        if (cnt >= MAX_ISSUES_PER_DAY) throw new Error("daily_limit_reached")
        return cnt + 1
      })

      const newIssue = {
        issueType,
        description,
        brand,
        companyName,
        grade,
        date: new Date().toLocaleString("en-In", { timeZone: "Asia/Kolkata" }),
        status: "Pending",
        contactInfo,
        siteAddress,
        dispatchDetails,
      }

      const newIssueRef = await push(issuesRef, newIssue)
      const issuesId = newIssueRef.key

      if (issuesId) await sendNotification(issuesId, companyName)

      setSuccess(true)
      setConfirmation(true)

      // Reset form
      setIssueType("")
      setDescription("")
      setBrand(orderDetails.brand || "")
      setGrade(orderDetails.grade || "")
      setSiteAddress(orderDetails.siteAddress || "")
      setDispatchDetails(orderDetails.dispatchDetails || "")
      setAttachment(null)
      setError("")

      setTimeout(() => {
        setSuccess(false)
        setConfirmation(false)
      }, 2500)
    } catch (err) {
      console.error("Error submitting issue:", err)
      setError(err.message === "daily_limit_reached" 
        ? "Daily limit reached. Please try again tomorrow." 
        : "Error submitting issue")
    }
  }

  const issueInfo = () => {
    alert("This page allows you to submit various types of issues. You can track your previous submissions in the history panel.")
  }

  const renderLimitInfo = () => (
    <div className={`limit-info ${remainingIssues <= 2 ? 'limit-warning' : ''}`}>
      {remainingIssues > 0 ? (
        <>You have <strong>{remainingIssues}</strong> submissions left today (Max 10/day)</>
      ) : (
        <>Daily limit reached — come back tomorrow</>
      )}
    </div>
  )

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    )
  }

  return (
    <div className="raise-issue-page">
      <div className="raise-page-header">
        <h2>
          Raise an Issue {" "}
          <FaInfoCircle onClick={issueInfo} className="info-icon" />
        </h2>
      </div>
      <div className="raise-content-grid">
        {/* Form Section */}
        <div className="form-card">
          <form onSubmit={handleSubmit}>
            <div className="raise-form-grid">
              <div className="raise-input-group">
                <label>Brand</label>
                <select value={brand} onChange={(e) => setBrand(e.target.value)} required>
                  <option value="">Select Brand</option>
                  <option value="Ultratech">Ultratech</option>
                  <option value="Shree">Shree</option>
                  <option value="JSW">JSW</option>
                  <option value="ACC">ACC</option>
                  <option value="JKSuper">JK Super</option>
                  <option value="Ambuja">Ambuja</option>
                </select>
              </div>

              <div className="raise-input-group">
                <label>Grade</label>
                <select value={grade} onChange={(e) => setGrade(e.target.value)} required>
                  <option value="">Select Grade</option>
                  <option value="OPC43">OPC43</option>
                  <option value="OPC53">OPC53</option>
                  <option value="PSC">PSC</option>
                  <option value="PPC">PPC</option>
                </select>
              </div>

              <div className="raise-input-group full">
                <label>Site Address</label>
                <input
                  type="text"
                  value={siteAddress}
                  onChange={(e) => setSiteAddress(e.target.value)}
                  minLength={5}
                  maxLength={150}
                  required
                />
              </div>

              <div className="raise-input-group full">
                <label>Dispatch Details</label>
                <input type="text" value={dispatchDetails} readOnly className="readonly" />
              </div>

              <div className="raise-input-group full">
                <label>Issue Type</label>
                <select value={issueType} onChange={(e) => setIssueType(e.target.value)} required>
                  <option value="">Select Issue Type</option>
                  <option value="Technical">Technical Issue</option>
                  <option value="Order/Delivery">Order/Delivery Issue</option>
                  <option value="Payment/Billing">Payment/Billing Issue</option>
                  <option value="Suggestion">Suggestion</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="raise-input-group full">
                <label>Description <span className="char-count">({description.replace(/\s/g, "").length}/100)</span></label>
                <textarea
                  rows="3"
                  placeholder="Describe your issue in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  minLength={10}
                  maxLength={100}
                  required
                />
              </div>
            </div>

            {renderLimitInfo()}

            {error && (
              <div className="alert error">
                <FaExclamationTriangle /> {error}
              </div>
            )}

            {success && (
              <div className="alert success">
                <FaCheckCircle /> Issue submitted successfully!
              </div>
            )}

            <button type="submit" className="btn-submit" disabled={remainingIssues <= 0}>
              Submit Issue
            </button>
          </form>
        </div>

        {/* History Section */}
        <div className="raise-history-card">
          <h4>Previous Issues ({issuesHistory.length})</h4>
          
          <div className="history-list">
            {issuesHistory.length > 0 ? (
              issuesHistory.map((issue, index) => (
                <div className="history-item" key={index}>
                  <div className="history-header">
                    <h3>{issue.issueType}</h3>
                    <span className={`status-badge ${issue.status === "Pending" ? "pending" : "resolved"}`}>
                      {issue.status}
                    </span>
                  </div>
                  <p><strong>Brand:</strong> {issue.brand} • <strong>Grade:</strong> {issue.grade}</p>
                  <p className="date">{issue.date}</p>
                  <p className="description">{issue.description}</p>
                </div>
              ))
            ) : (
              <p className="no-data">No issues submitted yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RaiseIssuePage